import { Trash2Icon } from "lucide-react";
import type * as React from "react";
import { useRef, useState } from "react";
import { Loader } from "@/components/ui/atoms/loader";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/atoms/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/atoms/tooltip";
import { ConfirmPopover } from "@/components/ui/molecules/confirm-popover";
import { cn } from "@/lib/utils";

export type ChatHistoryItemProps = {
  /** Chat title displayed in the sidebar item. */
  title: string;
  /** Whether this item is the currently active chat. */
  isActive?: boolean;
  /** Whether the chat is currently streaming a response. Shows a loading indicator. */
  isStreaming?: boolean;
  /** When set, the item renders as a link instead of a button. */
  href?: string;
  /** Router Link component for client-side routing. Receives `to` and `children`. When omitted, renders a native `<a>` with `href`. */
  linkComponent?: React.ElementType;
  /** Called when the item is clicked (for side-effects like stopping a stream). When `href` is set, navigation is handled by the link. */
  onSelect?: () => void;
  /** Called when the user confirms deletion. When set, a delete action appears on hover. */
  onDelete?: () => void;
  /** Icon component for the delete action. @default Trash2Icon from lucide-react */
  deleteIcon?: React.ReactNode;
};

export function ChatHistoryItem({
  title,
  isActive,
  isStreaming = false,
  href,
  linkComponent: LinkComp,
  onSelect,
  onDelete,
  deleteIcon,
}: ChatHistoryItemProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const handleTooltipOpenChange = (open: boolean) => {
    if (!open) {
      setTooltipOpen(false);
      return;
    }
    if (
      textRef.current &&
      textRef.current.scrollWidth > textRef.current.offsetWidth
    ) {
      setTooltipOpen(true);
    }
  };

  const buttonClassName = cn(
    "py-2.5 group-hover/menu-item:bg-sidebar-accent group-hover/menu-item:text-sidebar-accent-foreground",
    (onDelete || isStreaming) && "pr-8"
  );

  const content = (
    <span className="truncate" ref={textRef}>
      {title}
    </span>
  );

  return (
    <SidebarMenuItem>
      <Tooltip onOpenChange={handleTooltipOpenChange} open={tooltipOpen}>
        <TooltipTrigger
          render={
            href ? (
              <SidebarMenuButton
                className={buttonClassName}
                isActive={isActive}
                render={
                  LinkComp ? (
                    <LinkComp onClick={onSelect} to={href} />
                  ) : (
                    // biome-ignore lint/a11y/useAnchorContent: children rendered by Base UI render prop
                    <a href={href} onClick={onSelect} />
                  )
                }
              />
            ) : (
              <SidebarMenuButton
                className={buttonClassName}
                isActive={isActive}
                onClick={onSelect}
              />
            )
          }
        >
          {content}
        </TooltipTrigger>
        <TooltipContent side="right">{title}</TooltipContent>
      </Tooltip>
      {isStreaming ? (
        <SidebarMenuAction
          aria-hidden="true"
          className="pointer-events-none"
          tabIndex={-1}
        >
          <Loader
            className="border-sidebar-foreground/25 border-t-sidebar-foreground"
            size="sm"
            variant="circular"
          />
        </SidebarMenuAction>
      ) : (
        onDelete && (
          <ConfirmPopover
            confirmLabel="Delete"
            description="This action cannot be undone."
            onConfirm={onDelete}
            side="right"
            title="Delete this chat?"
          >
            <SidebarMenuAction
              aria-label={`Delete "${title}"`}
              className="hover:bg-secondary hover:text-secondary-foreground"
              showOnHover
            >
              {deleteIcon ?? <Trash2Icon className="size-3.5" />}
            </SidebarMenuAction>
          </ConfirmPopover>
        )
      )}
    </SidebarMenuItem>
  );
}
